import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { ShieldCheck, User, Mail } from 'lucide-react';
import { RootState } from '../../redux/store';
import { userApi } from '../../services/userApi';
import '../customer/ProfileManagementPage.css';

const AdminProfilePage: React.FC = () => {
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const [is2faEnabled, setIs2faEnabled] = useState(false);
  const [is2faUpdating, setIs2faUpdating] = useState(false);
  const [twoFactorMessage, setTwoFactorMessage] = useState('');
  const [twoFactorSetup, setTwoFactorSetup] = useState<{
    qrCodeDataUrl: string;
    manualEntryKey: string;
  } | null>(null);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [isDisabling2fa, setIsDisabling2fa] = useState(false);

  useEffect(() => {
    setIs2faEnabled(!!currentUser?.twoFactorEnabled);

    const fetchProfile = async () => {
      try {
        const response = await userApi.getProfile();
        setIs2faEnabled(!!response.data.user.twoFactorEnabled);
      } catch (error) {
        console.error('Failed to fetch admin profile settings', error);
      }
    };

    fetchProfile();
  }, [currentUser]);

  const handleStartTwoFactorSetup = async () => {
    setIs2faUpdating(true);
    setTwoFactorMessage('');
    setTwoFactorSetup(null);
    setTwoFactorCode('');

    try {
      const response = await userApi.startTwoFactorSetup();
      setTwoFactorSetup({
        qrCodeDataUrl: response.data.qrCodeDataUrl,
        manualEntryKey: response.data.manualEntryKey,
      });
      setTwoFactorMessage(response.message);
    } catch (error) {
      console.error('Failed to start admin 2FA setup', error);
      setTwoFactorMessage('Unable to start authenticator setup. Please try again later.');
    } finally {
      setIs2faUpdating(false);
    }
  };

  const handleConfirmTwoFactorSetup = async () => {
    if (!/^\d{6}$/.test(twoFactorCode)) {
      setTwoFactorMessage('Enter the 6-digit code from your authenticator app.');
      return;
    }

    setIs2faUpdating(true);
    setTwoFactorMessage('');

    try {
      const response = await userApi.confirmTwoFactorSetup({ twoFactorCode });
      setIs2faEnabled(!!response.data.user.twoFactorEnabled);
      setTwoFactorSetup(null);
      setTwoFactorCode('');
      setTwoFactorMessage(response.message);
    } catch (error) {
      console.error('Failed to confirm admin 2FA setup', error);
      setTwoFactorMessage('Invalid authenticator code. Check the app code and try again.');
    } finally {
      setIs2faUpdating(false);
    }
  };

  const handleDisableTwoFactor = async () => {
    if (!isDisabling2fa) {
      setIsDisabling2fa(true);
      setTwoFactorSetup(null);
      setTwoFactorCode('');
      setTwoFactorMessage('Enter a current authenticator code to disable 2FA.');
      return;
    }

    if (!/^\d{6}$/.test(twoFactorCode)) {
      setTwoFactorMessage('Enter the 6-digit code from your authenticator app.');
      return;
    }

    setIs2faUpdating(true);
    setTwoFactorMessage('');

    try {
      const response = await userApi.disableTwoFactor({ twoFactorCode });
      setIs2faEnabled(!!response.data.user.twoFactorEnabled);
      setIsDisabling2fa(false);
      setTwoFactorCode('');
      setTwoFactorMessage(response.message);
    } catch (error) {
      console.error('Failed to disable admin 2FA', error);
      setTwoFactorMessage('Invalid authenticator code. 2FA was not disabled.');
    } finally {
      setIs2faUpdating(false);
    }
  };

  const handleCancelTwoFactorAction = () => {
    setTwoFactorSetup(null);
    setIsDisabling2fa(false);
    setTwoFactorCode('');
    setTwoFactorMessage('');
  };

  return (
    <div className="profile-management-page">
      <div className="page-header">
        <h2>Admin Profile</h2>
        <p>Manage your administrator account and security settings</p>
      </div>

      <div className="profile-container">
        <div className="profile-header">
          <h3>Account Details</h3>
        </div>

        <div className="profile-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="admin-name">Name</label>
              <div className="relative">
                <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-fintech-textMuted" />
                <input id="admin-name" type="text" value={currentUser?.name || ''} disabled className="!pl-10" />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="admin-email">Email</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-fintech-textMuted" />
                <input id="admin-email" type="email" value={currentUser?.email || ''} disabled className="!pl-10" />
              </div>
            </div>
          </div>

          <div id="admin-two-factor" className="two-factor-section mb-6 p-5 rounded-xl border border-slate-200 bg-slate-50">
            <div className="flex items-center justify-between mb-3 gap-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-50 border border-blue-100 text-fintech-secondary flex items-center justify-center flex-shrink-0">
                  <ShieldCheck size={18} />
                </div>
                <div>
                  <h4 className="text-lg font-semibold">Authenticator App 2FA</h4>
                  <p className="text-sm text-slate-600">
                    Require a 6-digit authenticator app code when signing in as admin.
                  </p>
                </div>
              </div>
              {!twoFactorSetup && !isDisabling2fa && (
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={is2faEnabled ? handleDisableTwoFactor : handleStartTwoFactorSetup}
                  disabled={is2faUpdating}
                >
                  {is2faUpdating ? 'Saving...' : is2faEnabled ? 'Disable 2FA' : 'Set up 2FA'}
                </button>
              )}
            </div>

            <p className="text-sm">
              Status: <span className="font-semibold">{is2faEnabled ? 'Enabled' : 'Disabled'}</span>
            </p>

            {twoFactorSetup && (
              <div className="two-factor-setup">
                <img src={twoFactorSetup.qrCodeDataUrl} alt="Authenticator app QR code" />
                <div className="two-factor-setup-details">
                  <p>Scan this QR code in your authenticator app, then enter the 6-digit code.</p>
                  <div className="manual-key">
                    <span>Manual key</span>
                    <code>{twoFactorSetup.manualEntryKey}</code>
                  </div>
                  <div className="two-factor-code-row">
                    <input
                      type="text"
                      inputMode="numeric"
                      value={twoFactorCode}
                      onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="123456"
                      maxLength={6}
                      aria-label="Authenticator code"
                    />
                    <button type="button" className="save-btn" onClick={handleConfirmTwoFactorSetup} disabled={is2faUpdating}>
                      Confirm
                    </button>
                    <button type="button" className="cancel-btn" onClick={handleCancelTwoFactorAction} disabled={is2faUpdating}>
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            {isDisabling2fa && (
              <div className="two-factor-disable">
                <input
                  type="text"
                  inputMode="numeric"
                  value={twoFactorCode}
                  onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Authenticator code"
                  maxLength={6}
                  aria-label="Authenticator code"
                />
                <button type="button" className="remove-director-btn" onClick={handleDisableTwoFactor} disabled={is2faUpdating}>
                  Disable
                </button>
                <button type="button" className="cancel-btn" onClick={handleCancelTwoFactorAction} disabled={is2faUpdating}>
                  Cancel
                </button>
              </div>
            )}

            {twoFactorMessage && (
              <p className="mt-3 text-sm text-slate-700">{twoFactorMessage}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminProfilePage;
